
import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from "@/components/ui/table";
import { OddsRange } from '@/types/teamTypes';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { MatchDetailsList } from '@/components/team-details/match-details/MatchDetailsList';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronDown, ChevronUp, List, Filter } from 'lucide-react';
import { 
  calculateProfitForOddsRange, 
  calculateLayProfitForOddsRange
} from '@/components/odds-analysis/utils/basicOddsCalculations';
import { filterMatchesByOddsRange } from '@/components/odds-analysis/utils/helpers';
import {
  calculateGoalsAverageForOddsRange,
  calculateOverUnderGoalsForOddsRange
} from '@/components/odds-analysis/utils/goalsCalculations';
import {
  calculateOver25ProfitForRange,
  calculateUnder25ProfitForRange,
  getOverUnderProfitBreakdown
} from '@/components/odds-analysis/utils/overUnderCalculations';
import {
  calculateOver25LayProfitForRange,
  calculateUnder25LayProfitForRange,
  getOver25LayProfitBreakdown,
  getUnder25LayProfitBreakdown
} from '@/components/odds-analysis/utils/layCalculations';
import { formatDate } from '@/utils/dateTimeFormatters';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface OddsResultsTableProps {
  homeOddsRanges: OddsRange[];
  awayOddsRanges: OddsRange[];
  matches?: any[];
  selectedType?: 'home' | 'away';
  onTypeChange?: (type: 'home' | 'away') => void;
}

export const OddsResultsTable: React.FC<OddsResultsTableProps> = ({ 
  homeOddsRanges, 
  awayOddsRanges,
  matches = [],
  selectedType = 'home',
  onTypeChange
}) => {
  const [selectedRangeLabel, setSelectedRangeLabel] = useState<string | null>(null);
  const [selectedOutcome, setSelectedOutcome] = useState<'wins' | 'draws' | 'losses' | null>(null);
  const [selectedTabType, setSelectedTabType] = useState<'home' | 'away'>(selectedType);
  const [filteredMatches, setFilteredMatches] = useState<any[]>([]);
  const [selectedRangeMatches, setSelectedRangeMatches] = useState<any[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    'range', 'matches', 'wins', 'winPercent', 'draws', 'drawPercent', 'losses', 'lossPercent',
    'backPL', 'layPL', 'over25PL', 'under25PL', 'over25LayPL', 'under25LayPL'
  ]);

  useEffect(() => {
    setSelectedTabType(selectedType);
  }, [selectedType]);

  const toggleColumn = (columnId: string) => {
    if (visibleColumns.includes(columnId)) {
      if (visibleColumns.length > 4) { // Keep at least 4 columns visible
        setVisibleColumns(visibleColumns.filter(col => col !== columnId));
      }
    } else {
      setVisibleColumns([...visibleColumns, columnId]);
    }
  };

  const isColumnVisible = (columnId: string) => visibleColumns.includes(columnId);

  const handleCellClick = (rangeLabel: string, outcome: 'wins' | 'draws' | 'losses', oddsType: 'home' | 'away') => {
    const newRangeLabel = selectedRangeLabel === rangeLabel && selectedOutcome === outcome && selectedTabType === oddsType 
      ? null 
      : rangeLabel;
    
    const newOutcome = selectedRangeLabel === rangeLabel && selectedOutcome === outcome && selectedTabType === oddsType 
      ? null 
      : outcome;
    
    setSelectedRangeLabel(newRangeLabel);
    setSelectedOutcome(newOutcome);
    setSelectedTabType(oddsType);
    
    if (onTypeChange) {
      onTypeChange(oddsType);
    }
    
    if (newRangeLabel && newOutcome && matches) {
      const ranges = oddsType === 'home' ? homeOddsRanges : awayOddsRanges;
      const selectedRange = ranges.find(range => range.label === newRangeLabel);
      
      if (selectedRange) {
        const matchesInRange = filterMatchesByOddsRange(
          matches,
          { min: selectedRange.min, max: selectedRange.max },
          oddsType
        );
        
        const filtered = matchesInRange.filter(match => {
          const isHomeTeamOdds = oddsType === 'home';
          
          switch (newOutcome) {
            case 'wins':
              return isHomeTeamOdds 
                ? match.FTR === 'H' // Home team won
                : match.FTR === 'A'; // Away team won
            case 'draws':
              return match.FTR === 'D'; // Draw
            case 'losses':
              return isHomeTeamOdds
                ? match.FTR !== 'H' // Home team didn't win
                : match.FTR !== 'A'; // Away team didn't win
            default:
              return true;
          }
        });
        
        setSelectedRangeMatches(filtered);
        
        const outcomeText = newOutcome === 'wins' 
          ? (oddsType === 'home' ? 'Home Wins' : 'Away Wins') 
          : newOutcome === 'draws' 
            ? 'Draws' 
            : (oddsType === 'home' ? 'Home Losses' : 'Away Losses');
            
        setDialogTitle(`${filtered.length} ${outcomeText} with ${oddsType === 'home' ? 'Home' : 'Away'} odds range ${rangeLabel}`);
        setIsDialogOpen(true);
      }
    } else {
      setFilteredMatches([]);
      setSelectedRangeMatches([]);
    }
  };

  const handleMatchesClick = (rangeLabel: string, oddsType: 'home' | 'away') => {
    const ranges = oddsType === 'home' ? homeOddsRanges : awayOddsRanges;
    const selectedRange = ranges.find(range => range.label === rangeLabel);
    
    if (selectedRange && matches) {
      const filtered = filterMatchesByOddsRange(
        matches,
        { min: selectedRange.min, max: selectedRange.max },
        oddsType
      );
      
      setSelectedRangeMatches(filtered);
      setDialogTitle(`${filtered.length} Matches with ${oddsType === 'home' ? 'Home' : 'Away'} odds range ${rangeLabel}`);
      setIsDialogOpen(true);
    }
  };

  const calculateProfitLoss = (range: OddsRange, oddsType: 'home' | 'away'): number => {
    if (!matches || matches.length === 0) return 0;
    
    return calculateProfitForOddsRange(
      matches,
      { min: range.min, max: range.max },
      oddsType
    );
  };

  const calculateLayProfitLoss = (range: OddsRange, oddsType: 'home' | 'away'): number => {
    if (!matches || matches.length === 0) return 0;
    
    return calculateLayProfitForOddsRange(
      matches,
      { min: range.min, max: range.max },
      oddsType
    );
  };

  const calculateTotalProfit = (oddsRanges: OddsRange[], oddsType: 'home' | 'away'): number => {
    if (!matches || matches.length === 0) return 0;
    
    let totalProfit = 0;
    oddsRanges.forEach(range => {
      totalProfit += calculateProfitForOddsRange(
        matches,
        { min: range.min, max: range.max },
        oddsType
      );
    });
    
    return totalProfit;
  };

  const calculateTotalLayProfit = (oddsRanges: OddsRange[], oddsType: 'home' | 'away'): number => {
    if (!matches || matches.length === 0) return 0;
    
    let totalLayProfit = 0;
    oddsRanges.forEach(range => {
      totalLayProfit += calculateLayProfitForOddsRange(
        matches,
        { min: range.min, max: range.max },
        oddsType
      );
    });
    
    return totalLayProfit;
  };

  const calculateOver25Profit = (range: OddsRange, oddsType: 'home' | 'away'): number => {
    if (!matches || matches.length === 0) return 0;
    
    return calculateOver25ProfitForRange(
      matches,
      { min: range.min, max: range.max },
      oddsType
    );
  };

  const calculateUnder25Profit = (range: OddsRange, oddsType: 'home' | 'away'): number => {
    if (!matches || matches.length === 0) return 0;
    
    return calculateUnder25ProfitForRange(
      matches,
      { min: range.min, max: range.max },
      oddsType
    );
  };

  const calculateTotalOver25Profit = (oddsRanges: OddsRange[], oddsType: 'home' | 'away'): number => {
    if (!matches || matches.length === 0) return 0;
    
    let totalProfit = 0;
    oddsRanges.forEach(range => {
      totalProfit += calculateOver25ProfitForRange(
        matches,
        { min: range.min, max: range.max },
        oddsType
      );
    });
    
    return totalProfit;
  };

  const calculateTotalUnder25Profit = (oddsRanges: OddsRange[], oddsType: 'home' | 'away'): number => {
    if (!matches || matches.length === 0) return 0;
    
    let totalProfit = 0;
    oddsRanges.forEach(range => {
      totalProfit += calculateUnder25ProfitForRange(
        matches,
        { min: range.min, max: range.max },
        oddsType
      );
    });
    
    return totalProfit;
  };

  const calculateOver25LayProfit = (range: OddsRange, oddsType: 'home' | 'away'): number => {
    if (!matches || matches.length === 0) return 0;
    
    return calculateOver25LayProfitForRange(
      matches,
      { min: range.min, max: range.max },
      oddsType
    );
  };

  const calculateUnder25LayProfit = (range: OddsRange, oddsType: 'home' | 'away'): number => {
    if (!matches || matches.length === 0) return 0;
    
    return calculateUnder25LayProfitForRange(
      matches,
      { min: range.min, max: range.max },
      oddsType
    );
  };

  const calculateTotalOver25LayProfit = (oddsRanges: OddsRange[], oddsType: 'home' | 'away'): number => {
    if (!matches || matches.length === 0) return 0;
    
    let totalProfit = 0;
    oddsRanges.forEach(range => {
      totalProfit += calculateOver25LayProfitForRange(
        matches,
        { min: range.min, max: range.max },
        oddsType
      );
    });
    
    return totalProfit;
  };

  const calculateTotalUnder25LayProfit = (oddsRanges: OddsRange[], oddsType: 'home' | 'away'): number => {
    if (!matches || matches.length === 0) return 0;
    
    let totalProfit = 0;
    oddsRanges.forEach(range => {
      totalProfit += calculateUnder25LayProfitForRange(
        matches,
        { min: range.min, max: range.max },
        oddsType
      );
    });
    
    return totalProfit;
  };

  const getProfitBreakdown = (range: { min: number, max: number }, oddsType: 'home' | 'away'): { wins: number, losses: number } => {
    if (!matches || matches.length === 0) return { wins: 0, losses: 0 };
    
    const matchesInRange = filterMatchesByOddsRange(matches, range, oddsType);
    let winnings = 0;
    let lossAmount = 0;
    
    matchesInRange.forEach(match => {
      const oddsField = oddsType === 'home' ? 'B365H' : 'B365A';
      const oddsValue = parseFloat(match[oddsField]);
      
      if (!isNaN(oddsValue)) {
        const isHomeTeamOdds = oddsType === 'home';
        
        if ((isHomeTeamOdds && match.FTR === 'H') || (!isHomeTeamOdds && match.FTR === 'A')) {
          winnings += (oddsValue - 1);
        } else {
          lossAmount += 1;
        }
      }
    });
    
    return { wins: winnings, losses: lossAmount };
  };

  const getLayProfitBreakdown = (range: { min: number, max: number }, oddsType: 'home' | 'away'): { wins: number, losses: number } => {
    if (!matches || matches.length === 0) return { wins: 0, losses: 0 };
    
    const matchesInRange = filterMatchesByOddsRange(matches, range, oddsType);
    let winnings = 0;
    let lossAmount = 0;
    const layMarkup = 0.1;
    
    matchesInRange.forEach(match => {
      const oddsField = oddsType === 'home' ? 'B365H' : 'B365A';
      const oddsValue = parseFloat(match[oddsField]);
      
      if (!isNaN(oddsValue)) {
        const isHomeTeamOdds = oddsType === 'home';
        const layOdds = oddsValue + layMarkup;
        
        if ((isHomeTeamOdds && match.FTR === 'H') || (!isHomeTeamOdds && match.FTR === 'A')) {
          lossAmount += (layOdds - 1);
        } else {
          winnings += 1;
        }
      }
    });
    
    return { wins: winnings, losses: lossAmount };
  };

  const getLocalOver25LayProfitBreakdown = (range: { min: number, max: number }, oddsType: 'home' | 'away'): { wins: number, losses: number } => {
    if (!matches || matches.length === 0) return { wins: 0, losses: 0 };
    
    return getOver25LayProfitBreakdown(range, oddsType, matches);
  };

  const getLocalUnder25LayProfitBreakdown = (range: { min: number, max: number }, oddsType: 'home' | 'away'): { wins: number, losses: number } => {
    if (!matches || matches.length === 0) return { wins: 0, losses: 0 };
    
    return getUnder25LayProfitBreakdown(range, oddsType, matches);
  };

  const enhanceMatchWithOddsData = (match: any) => {
    if (match.Date) {
      match.Date = formatDate(match.Date);
    }
    
    const homeOddsFields = {
      B365H: 'Bet365 Home',
      BWH: 'BetWin Home',
      IWH: 'Interwetten Home',
      PSH: 'Pinnacle Home',
      WHH: 'William Hill Home',
      VCH: 'VC Bet Home'
    };
    
    const awayOddsFields = {
      B365A: 'Bet365 Away',
      BWA: 'BetWin Away',
      IWA: 'Interwetten Away',
      PSA: 'Pinnacle Away',
      WHA: 'William Hill Away',
      VCA: 'VC Bet Away'
    };
    
    const drawOddsFields = {
      B365D: 'Bet365 Draw',
      BWD: 'BetWin Draw',
      IWD: 'Interwetten Draw',
      PSD: 'Pinnacle Draw',
      WHD: 'William Hill Draw',
      VCD: 'VC Bet Draw'
    };
    
    const oddsData: Record<string, string> = {};
    
    [...Object.entries(homeOddsFields), ...Object.entries(awayOddsFields), ...Object.entries(drawOddsFields)].forEach(([key, label]) => {
      if (match[key] && !isNaN(parseFloat(match[key]))) {
        oddsData[key] = `${label}: ${match[key]}`;
      }
    });
    
    return {
      ...match,
      oddsData
    };
  };

  const renderTable = (oddsRanges: OddsRange[], oddsType: 'home' | 'away') => {
    const totalProfit = calculateTotalProfit(oddsRanges, oddsType);
    const totalLayProfit = calculateTotalLayProfit(oddsRanges, oddsType);
    const totalOver25Profit = calculateTotalOver25Profit(oddsRanges, oddsType);
    const totalUnder25Profit = calculateTotalUnder25Profit(oddsRanges, oddsType);
    const totalOver25LayProfit = calculateTotalOver25LayProfit(oddsRanges, oddsType);
    const totalUnder25LayProfit = calculateTotalUnder25LayProfit(oddsRanges, oddsType);
    
    let totalWinnings = 0;
    let totalLosses = 0;
    let totalLayWinnings = 0;
    let totalLayLosses = 0;
    let totalOver25Winnings = 0;
    let totalOver25Losses = 0;
    let totalUnder25Winnings = 0;
    let totalUnder25Losses = 0;
    let totalOver25LayWinnings = 0;
    let totalOver25LayLosses = 0;
    let totalUnder25LayWinnings = 0;
    let totalUnder25LayLosses = 0;
    
    let allMatchesHomeGoals = 0;
    let allMatchesAwayGoals = 0;
    let allMatchesCount = 0;
    let totalOver25Matches = 0;
    
    oddsRanges.forEach(range => {
      const { wins, losses } = getProfitBreakdown({ min: range.min, max: range.max }, oddsType);
      totalWinnings += wins;
      totalLosses += losses;
      
      const layBreakdown = getLayProfitBreakdown({ min: range.min, max: range.max }, oddsType);
      totalLayWinnings += layBreakdown.wins;
      totalLayLosses += layBreakdown.losses;
      
      const overUnderBreakdown = getOverUnderProfitBreakdown({ min: range.min, max: range.max }, oddsType, matches);
      totalOver25Winnings += overUnderBreakdown.over.wins;
      totalOver25Losses += overUnderBreakdown.over.losses;
      totalUnder25Winnings += overUnderBreakdown.under.wins;
      totalUnder25Losses += overUnderBreakdown.under.losses;
      
      const matchesInRange = matches ? filterMatchesByOddsRange(
        matches,
        { min: range.min, max: range.max },
        oddsType
      ) : [];
      
      matchesInRange.forEach(match => {
        allMatchesHomeGoals += parseInt(match.FTHG) || 0;
        allMatchesAwayGoals += parseInt(match.FTAG) || 0;
        
        const homeGoals = parseInt(match.FTHG) || 0;
        const awayGoals = parseInt(match.FTAG) || 0;
        if (homeGoals + awayGoals > 2.5) {
          totalOver25Matches++;
        }
      });
      
      allMatchesCount += matchesInRange.length;
    });
    
    const overallHomeAvg = allMatchesCount > 0 ? allMatchesHomeGoals / allMatchesCount : 0;
    const overallAwayAvg = allMatchesCount > 0 ? allMatchesAwayGoals / allMatchesCount : 0;
    const overallTotalAvg = allMatchesCount > 0 ? (allMatchesHomeGoals + allMatchesAwayGoals) / allMatchesCount : 0;
    
    const overallOver25Percent = allMatchesCount > 0 ? (totalOver25Matches / allMatchesCount) * 100 : 0;
    const overallUnder25Percent = allMatchesCount > 0 ? ((allMatchesCount - totalOver25Matches) / allMatchesCount) * 100 : 0;
    
    return (
      <div className="relative">
        <div className="flex justify-end mb-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 gap-1">
                <Filter className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Columns</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2" align="end">
              <div className="grid gap-1">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isColumnVisible('range')}
                      onChange={() => {}} // Always keep the range column visible
                      disabled={true}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Range
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isColumnVisible('matches')}
                      onChange={() => toggleColumn('matches')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Matches
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isColumnVisible('wins')}
                      onChange={() => toggleColumn('wins')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Wins
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isColumnVisible('winPercent')}
                      onChange={() => toggleColumn('winPercent')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Win %
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isColumnVisible('draws')}
                      onChange={() => toggleColumn('draws')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Draws
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isColumnVisible('drawPercent')}
                      onChange={() => toggleColumn('drawPercent')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Draw %
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isColumnVisible('losses')}
                      onChange={() => toggleColumn('losses')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Losses
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isColumnVisible('lossPercent')}
                      onChange={() => toggleColumn('lossPercent')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Loss %
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isColumnVisible('homeGoals')}
                      onChange={() => toggleColumn('homeGoals')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Home Goals
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isColumnVisible('awayGoals')}
                      onChange={() => toggleColumn('awayGoals')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Away Goals
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isColumnVisible('totalGoals')}
                      onChange={() => toggleColumn('totalGoals')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Total Goals
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isColumnVisible('over25')}
                      onChange={() => toggleColumn('over25')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Over 2.5 %
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isColumnVisible('under25')}
                      onChange={() => toggleColumn('under25')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Under 2.5 %
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isColumnVisible('backPL')}
                      onChange={() => toggleColumn('backPL')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Back P/L
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isColumnVisible('layPL')}
                      onChange={() => toggleColumn('layPL')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Lay P/L
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isColumnVisible('over25PL')}
                      onChange={() => toggleColumn('over25PL')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Over 2.5 P/L
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isColumnVisible('under25PL')}
                      onChange={() => toggleColumn('under25PL')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Under 2.5 P/L
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isColumnVisible('over25LayPL')}
                      onChange={() => toggleColumn('over25LayPL')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Lay &gt;2.5 P/L
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isColumnVisible('under25LayPL')}
                      onChange={() => toggleColumn('under25LayPL')}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Lay &lt;2.5 P/L
                  </label>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        
        <div className="overflow-x-auto">
          <TooltipProvider>
            <Table className="w-full text-xs md:text-sm">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[80px] py-2">Odds Range</TableHead>
                  {isColumnVisible('matches') && (
                    <TableHead className="text-right py-2">Matches</TableHead>
                  )}
                  {isColumnVisible('wins') && (
                    <TableHead className="text-right py-2">{oddsType === 'home' ? 'Home' : 'Away'} Wins</TableHead>
                  )}
                  {isColumnVisible('winPercent') && (
                    <TableHead className="text-right py-2">Win %</TableHead>
                  )}
                  {isColumnVisible('draws') && (
                    <TableHead className="text-right py-2">Draws</TableHead>
                  )}
                  {isColumnVisible('drawPercent') && (
                    <TableHead className="text-right py-2">Draw %</TableHead>
                  )}
                  {isColumnVisible('losses') && (
                    <TableHead className="text-right py-2">{oddsType === 'home' ? 'Away' : 'Home'} Wins</TableHead>
                  )}
                  {isColumnVisible('lossPercent') && (
                    <TableHead className="text-right py-2">{oddsType === 'home' ? 'Away' : 'Home'} %</TableHead>
                  )}
                  {isColumnVisible('homeGoals') && (
                    <TableHead className="text-right py-2">Home Goals</TableHead>
                  )}
                  {isColumnVisible('awayGoals') && (
                    <TableHead className="text-right py-2">Away Goals</TableHead>
                  )}
                  {isColumnVisible('totalGoals') && (
                    <TableHead className="text-right py-2">Total Goals</TableHead>
                  )}
                  {isColumnVisible('over25') && (
                    <TableHead className="text-right py-2">Over 2.5 %</TableHead>
                  )}
                  {isColumnVisible('under25') && (
                    <TableHead className="text-right py-2">Under 2.5 %</TableHead>
                  )}
                  {isColumnVisible('backPL') && (
                    <TableHead className="text-right py-2">{oddsType === 'home' ? 'Home-Back' : 'Away-Back'} P/L</TableHead>
                  )}
                  {isColumnVisible('layPL') && (
                    <TableHead className="text-right py-2">Lay P/L</TableHead>
                  )}
                  {isColumnVisible('over25PL') && (
                    <TableHead className="text-right py-2">Back &gt;2.5 P/L</TableHead>
                  )}
                  {isColumnVisible('under25PL') && (
                    <TableHead className="text-right py-2">Back &lt;2.5 P/L</TableHead>
                  )}
                  {isColumnVisible('over25LayPL') && (
                    <TableHead className="text-right py-2">Lay &gt;2.5 P/L</TableHead>
                  )}
                  {isColumnVisible('under25LayPL') && (
                    <TableHead className="text-right py-2">Lay &lt;2.5 P/L</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {oddsRanges.map((range) => {
                  const matchesInRange = matches ? filterMatchesByOddsRange(
                    matches,
                    { min: range.min, max: range.max },
                    oddsType
                  ) : [];
                  
                  const totalMatchesInRange = matchesInRange.length;
                  
                  const teamWins = matchesInRange.filter(match => 
                    oddsType === 'home' ? match.FTR === 'H' : match.FTR === 'A'
                  ).length;
                  
                  const draws = matchesInRange.filter(match => match.FTR === 'D').length;
                  const losses = totalMatchesInRange - teamWins - draws;
                  
                  const winPercent = totalMatchesInRange > 0 ? Math.round((teamWins / totalMatchesInRange) * 100) : 0;
                  const drawPercent = totalMatchesInRange > 0 ? Math.round((draws / totalMatchesInRange) * 100) : 0;
                  const lossPercent = totalMatchesInRange > 0 ? Math.round((losses / totalMatchesInRange) * 100) : 0;
                  
                  const isSelected = selectedRangeLabel === range.label && selectedType === oddsType;
                  
                  const profitLoss = calculateProfitForOddsRange(matches || [], { min: range.min, max: range.max }, oddsType);
                  const isProfitable = profitLoss > 0;
                  
                  const layProfitLoss = calculateLayProfitForOddsRange(matches || [], { min: range.min, max: range.max }, oddsType);
                  const isLayProfitable = layProfitLoss > 0;
                  
                  const { wins, losses: lossAmount } = getProfitBreakdown({ min: range.min, max: range.max }, oddsType);
                  const layBreakdown = getLayProfitBreakdown({ min: range.min, max: range.max }, oddsType);
                  
                  const { homeAvg, awayAvg, totalAvg } = calculateGoalsAverageForOddsRange(
                    matches || [], 
                    { min: range.min, max: range.max }, 
                    oddsType
                  );
                  
                  const { over25Percent, under25Percent } = calculateOverUnderGoalsForOddsRange(
                    matches || [],
                    { min: range.min, max: range.max },
                    oddsType
                  );
                  
                  const over25Profit = calculateOver25ProfitForRange(
                    matches || [],
                    { min: range.min, max: range.max },
                    oddsType
                  );
                  const under25Profit = calculateUnder25ProfitForRange(
                    matches || [],
                    { min: range.min, max: range.max },
                    oddsType
                  );
                  const isOver25Profitable = over25Profit > 0;
                  const isUnder25Profitable = under25Profit > 0;
                  
                  const overUnderBreakdown = getOverUnderProfitBreakdown({ min: range.min, max: range.max }, oddsType, matches);
                  
                  const over25LayProfit = calculateOver25LayProfit(
                    range,
                    oddsType
                  );
                  const under25LayProfit = calculateUnder25LayProfit(
                    range,
                    oddsType
                  );
                  const isOver25LayProfitable = over25LayProfit > 0;
                  const isUnder25LayProfitable = under25LayProfit > 0;
                  
                  const over25LayBreakdown = getLocalOver25LayProfitBreakdown({ min: range.min, max: range.max }, oddsType);
                  const under25LayBreakdown = getLocalUnder25LayProfitBreakdown({ min: range.min, max: range.max }, oddsType);
                  
                  return (
                    <TableRow key={range.label} className={`${isSelected ? "bg-gray-100" : ""} py-1`}>
                      <TableCell className="font-medium whitespace-nowrap py-1">{range.label}</TableCell>
                      
                      {isColumnVisible('matches') && (
                        <TableCell 
                          className="text-right cursor-pointer hover:underline flex items-center justify-end gap-1 py-1"
                          onClick={() => handleMatchesClick(range.label, oddsType)}
                        >
                          {totalMatchesInRange} <List className="h-3 w-3" />
                        </TableCell>
                      )}
                      
                      {isColumnVisible('wins') && (
                        <TableCell 
                          className={`text-right font-medium cursor-pointer hover:underline py-1
                            ${winPercent >= 60 ? 'text-green-600' : winPercent <= 30 ? 'text-red-600' : ''}
                            ${isSelected && selectedOutcome === 'wins' ? 'bg-blue-100' : ''}`}
                          onClick={() => handleCellClick(range.label, 'wins', oddsType)}
                        >
                          {teamWins}
                        </TableCell>
                      )}
                      
                      {isColumnVisible('winPercent') && (
                        <TableCell className={`text-right font-medium py-1
                          ${winPercent >= 60 ? 'text-green-600' : winPercent <= 30 ? 'text-red-600' : ''}`}
                        >
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>{winPercent}%</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Wins: {teamWins}</p>
                              <p>Total matches: {totalMatchesInRange}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                      )}
                      
                      {isColumnVisible('draws') && (
                        <TableCell 
                          className={`text-right cursor-pointer hover:underline py-1
                            ${isSelected && selectedOutcome === 'draws' ? 'bg-blue-100' : ''}`}
                          onClick={() => handleCellClick(range.label, 'draws', oddsType)}
                        >
                          {draws}
                        </TableCell>
                      )}
                      
                      {isColumnVisible('drawPercent') && (
                        <TableCell className="text-right py-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>{drawPercent}%</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Draws: {draws}</p>
                              <p>Total matches: {totalMatchesInRange}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                      )}
                      
                      {isColumnVisible('losses') && (
                        <TableCell 
                          className={`text-right cursor-pointer hover:underline py-1
                            ${isSelected && selectedOutcome === 'losses' ? 'bg-blue-100' : ''}`}
                          onClick={() => handleCellClick(range.label, 'losses', oddsType)}
                        >
                          {losses}
                        </TableCell>
                      )}
                      
                      {isColumnVisible('lossPercent') && (
                        <TableCell className="text-right py-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>{lossPercent}%</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Losses: {losses}</p>
                              <p>Total matches: {totalMatchesInRange}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                      )}
                      
                      {isColumnVisible('homeGoals') && (
                        <TableCell className="text-right font-medium py-1">
                          {homeAvg.toFixed(2)}
                        </TableCell>
                      )}
                      
                      {isColumnVisible('awayGoals') && (
                        <TableCell className="text-right font-medium py-1">
                          {awayAvg.toFixed(2)}
                        </TableCell>
                      )}
                      
                      {isColumnVisible('totalGoals') && (
                        <TableCell className="text-right font-medium py-1">
                          {totalAvg.toFixed(2)}
                        </TableCell>
                      )}
                      
                      {isColumnVisible('over25') && (
                        <TableCell className={`text-right font-medium py-1 ${over25Percent >= 65 ? 'text-green-600' : over25Percent <= 35 ? 'text-red-600' : ''}`}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>{Math.round(over25Percent)}%</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Over 2.5 goals: {Math.round(over25Percent * totalMatchesInRange / 100)}</p>
                              <p>Total matches: {totalMatchesInRange}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                      )}
                      
                      {isColumnVisible('under25') && (
                        <TableCell className={`text-right font-medium py-1 ${under25Percent >= 65 ? 'text-green-600' : under25Percent <= 35 ? 'text-red-600' : ''}`}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>{Math.round(under25Percent)}%</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Under 2.5 goals: {Math.round(under25Percent * totalMatchesInRange / 100)}</p>
                              <p>Total matches: {totalMatchesInRange}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                      )}
                      
                      {isColumnVisible('backPL') && (
                        <TableCell className="text-right font-medium py-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={`${isProfitable ? 'text-green-600' : 'text-red-600'}`}>
                                £{profitLoss.toFixed(2)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Winnings: £{wins.toFixed(2)}</p>
                              <p>Losses: £{lossAmount.toFixed(2)}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                      )}
                      
                      {isColumnVisible('layPL') && (
                        <TableCell className="text-right font-medium py-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={`${isLayProfitable ? 'text-green-600' : 'text-red-600'}`}>
                                £{layProfitLoss.toFixed(2)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Winnings: £{layBreakdown.wins.toFixed(2)}</p>
                              <p>Losses: £{layBreakdown.losses.toFixed(2)}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                      )}
                      
                      {isColumnVisible('over25PL') && (
                        <TableCell className="text-right font-medium py-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={`${isOver25Profitable ? 'text-green-600' : 'text-red-600'}`}>
                                £{over25Profit.toFixed(2)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Winnings: £{overUnderBreakdown.over.wins.toFixed(2)}</p>
                              <p>Losses: £{overUnderBreakdown.over.losses.toFixed(2)}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                      )}
                      
                      {isColumnVisible('under25PL') && (
                        <TableCell className="text-right font-medium py-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={`${isUnder25Profitable ? 'text-green-600' : 'text-red-600'}`}>
                                £{under25Profit.toFixed(2)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Winnings: £{overUnderBreakdown.under.wins.toFixed(2)}</p>
                              <p>Losses: £{overUnderBreakdown.under.losses.toFixed(2)}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                      )}
                      
                      {isColumnVisible('over25LayPL') && (
                        <TableCell className="text-right font-medium py-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={`${isOver25LayProfitable ? 'text-green-600' : 'text-red-600'}`}>
                                £{over25LayProfit.toFixed(2)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Winnings: £{over25LayBreakdown.wins.toFixed(2)}</p>
                              <p>Losses: £{over25LayBreakdown.losses.toFixed(2)}</p>
                              <p>Lay odds: Backing odds + 0.1</p>
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                      )}
                      
                      {isColumnVisible('under25LayPL') && (
                        <TableCell className="text-right font-medium py-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={`${isUnder25LayProfitable ? 'text-green-600' : 'text-red-600'}`}>
                                £{under25LayProfit.toFixed(2)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Winnings: £{under25LayBreakdown.wins.toFixed(2)}</p>
                              <p>Losses: £{under25LayBreakdown.losses.toFixed(2)}</p>
                              <p>Lay odds: Backing odds + 0.1</p>
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={1} className="text-left font-bold py-2">Totals:</TableCell>
                  
                  {isColumnVisible('matches') && (
                    <TableCell className="text-right font-bold py-2">{allMatchesCount}</TableCell>
                  )}
                  
                  {isColumnVisible('wins') && (
                    <TableCell className="text-right font-bold py-2">-</TableCell>
                  )}
                  
                  {isColumnVisible('winPercent') && (
                    <TableCell className="text-right font-bold py-2">-</TableCell>
                  )}
                  
                  {isColumnVisible('draws') && (
                    <TableCell className="text-right font-bold py-2">-</TableCell>
                  )}
                  
                  {isColumnVisible('drawPercent') && (
                    <TableCell className="text-right font-bold py-2">-</TableCell>
                  )}
                  
                  {isColumnVisible('losses') && (
                    <TableCell className="text-right font-bold py-2">-</TableCell>
                  )}
                  
                  {isColumnVisible('lossPercent') && (
                    <TableCell className="text-right font-bold py-2">-</TableCell>
                  )}
                  
                  {isColumnVisible('homeGoals') && (
                    <TableCell className="text-right font-bold py-2">{overallHomeAvg.toFixed(2)}</TableCell>
                  )}
                  
                  {isColumnVisible('awayGoals') && (
                    <TableCell className="text-right font-bold py-2">{overallAwayAvg.toFixed(2)}</TableCell>
                  )}
                  
                  {isColumnVisible('totalGoals') && (
                    <TableCell className="text-right font-bold py-2">{overallTotalAvg.toFixed(2)}</TableCell>
                  )}
                  
                  {isColumnVisible('over25') && (
                    <TableCell className="text-right font-bold py-2">-</TableCell>
                  )}
                  
                  {isColumnVisible('under25') && (
                    <TableCell className="text-right font-bold py-2">-</TableCell>
                  )}
                  
                  {isColumnVisible('backPL') && (
                    <TableCell className="text-right font-bold py-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className={`${totalProfit > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            £{totalProfit.toFixed(2)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Winnings: £{totalWinnings.toFixed(2)}</p>
                          <p>Losses: £{totalLosses.toFixed(2)}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                  )}
                  
                  {isColumnVisible('layPL') && (
                    <TableCell className="text-right font-bold py-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className={`${totalLayProfit > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            £{totalLayProfit.toFixed(2)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Winnings: £{totalLayWinnings.toFixed(2)}</p>
                          <p>Losses: £{totalLayLosses.toFixed(2)}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                  )}
                  
                  {isColumnVisible('over25PL') && (
                    <TableCell className="text-right font-bold py-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className={`${totalOver25Profit > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            £{totalOver25Profit.toFixed(2)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Winnings: £{totalOver25Winnings.toFixed(2)}</p>
                          <p>Losses: £{totalOver25Losses.toFixed(2)}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                  )}
                  
                  {isColumnVisible('under25PL') && (
                    <TableCell className="text-right font-bold py-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className={`${totalUnder25Profit > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            £{totalUnder25Profit.toFixed(2)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Winnings: £{totalUnder25Winnings.toFixed(2)}</p>
                          <p>Losses: £{totalUnder25Losses.toFixed(2)}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                  )}
                  
                  {isColumnVisible('over25LayPL') && (
                    <TableCell className="text-right font-bold py-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className={`${totalOver25LayProfit > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            £{totalOver25LayProfit.toFixed(2)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Winnings: £{totalOver25LayWinnings.toFixed(2)}</p>
                          <p>Losses: £{totalOver25LayLosses.toFixed(2)}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                  )}
                  
                  {isColumnVisible('under25LayPL') && (
                    <TableCell className="text-right font-bold py-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className={`${totalUnder25LayProfit > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            £{totalUnder25LayProfit.toFixed(2)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Winnings: £{totalUnder25LayWinnings.toFixed(2)}</p>
                          <p>Losses: £{totalUnder25LayLosses.toFixed(2)}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              </TableFooter>
            </Table>
          </TooltipProvider>
        </div>
      </div>
    );
  };

  const handleTabChange = (value: string) => {
    const newType = value as 'home' | 'away';
    setSelectedTabType(newType);
    if (onTypeChange) {
      onTypeChange(newType);
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="p-4">
        <CardTitle>Odds Analysis</CardTitle>
        <CardDescription>
          Analysis of team performance based on pre-match Bet365 odds.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4">
        <Tabs 
          defaultValue={selectedTabType} 
          value={selectedTabType}
          className="mb-6" 
          onValueChange={handleTabChange}
        >
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="home">Home Odds</TabsTrigger>
            <TabsTrigger value="away">Away Odds</TabsTrigger>
          </TabsList>
          <TabsContent value="home">
            {homeOddsRanges.length > 0 ? (
              renderTable(homeOddsRanges, 'home')
            ) : (
              <div className="text-center py-8 text-gray-500">
                No odds data available for the selected criteria
              </div>
            )}
          </TabsContent>
          <TabsContent value="away">
            {awayOddsRanges.length > 0 ? (
              renderTable(awayOddsRanges, 'away')
            ) : (
              <div className="text-center py-8 text-gray-500">
                No odds data available for the selected criteria
              </div>
            )}
          </TabsContent>
        </Tabs>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{dialogTitle}</DialogTitle>
              <DialogDescription>
                Showing detailed match information for the selected range
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <MatchDetailsList 
                matches={selectedRangeMatches.map(match => enhanceMatchWithOddsData(match))} 
                selectedStat={null}
                showOdds={true}
              />
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
